import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import React, { useContext, useEffect, useState } from 'react';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import {
  StripePayoutStatus,
  StripePayoutReconciliationReportRequest,
  StripePayoutReconciliationReportRow,
  StripePayoutReconciliationReportTransaction,
  StripePayoutReconciliationReportSummary,
} from '../../../clients/quipli-service/report/report';
import { useSearchParams } from 'react-router-dom';
import FileSaver from 'file-saver';
import { Typography } from '../../../components/typography/typography';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Card } from '../../../components/card/card';
import { Spinner } from '../../../components/spinner/spinner';
import { Paginator } from '../../../components/paginator/paginator-v2';
import { dineroSum, displayMoneyFormat, safeRound } from '../../../util/util';
import { DateTime } from 'luxon';
import _ from 'lodash';
import Dinero from 'dinero.js';
import { PaymentMethodFormat } from '../../collectibles/invoice-list';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';
// @hipaa-exclude
import { useQboApi } from '../../../clients/quipli-service/qbo/qbo-api-v1';
import { useAdmin } from '../../../hooks/use-admin-context';
import { TenantContext } from '../../../context/tenant-context';

function extractStripePayoutReconciliationReportSearchParams(
  params: URLSearchParams,
): StripePayoutReconciliationReportRequest {
  const from = params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined;
  const to = params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined;
  const pageNumber = params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined;
  const pageSize = params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined;
  return { from, to, pageNumber, pageSize };
}

function injectStripePayoutReconciliationReportParams(
  params: URLSearchParams,
  req: StripePayoutReconciliationReportRequest,
): URLSearchParams {
  req.from ? params.set('from', req.from.toISO()) : params.delete('from');
  req.to ? params.set('to', req.to.toISO()) : params.delete('to');
  req.pageNumber ? params.set('pageNumber', req.pageNumber.toString()) : params.delete('pageNumber');
  req.pageSize ? params.set('pageSize', req.pageSize.toString()) : params.delete('pageSize');

  return params;
}

export const StripePayoutReconciliationReport = () => {
  const {
    stripePayoutReconciliationReport,
    exportStripePayoutReconciliationReport,
    stripePayoutReconciliationSummary,
  } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();
  const [timezone, setTimezone] = useState<string>();
  const tc = useContext(TenantContext);
  const currency = tc.configuration?.currency || 'USD';
  const [enableQboPayoutSync, setEnableQboPayoutSync] = useState<boolean>(false);
  const [payoutReconciliationReport, setPayoutReconciliationReport] =
    useState<Pagination<StripePayoutReconciliationReportRow>>();

  const [payoutReconciliationSummaries, setPayoutReconciliationSummaries] =
    useState<StripePayoutReconciliationReportSummary[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportRequest, setReportRequest] = useState<StripePayoutReconciliationReportRequest>(
    extractStripePayoutReconciliationReportSearchParams(searchParams),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  async function loadReport() {
    if (timezone && Object.keys(reportRequest).length > 0) {
      setSearchParams(injectStripePayoutReconciliationReportParams(searchParams, reportRequest));
      setIsLoading(true);

      const report = await stripePayoutReconciliationReport(reportRequest);
      setPayoutReconciliationReport(report);

      const summary = await stripePayoutReconciliationSummary(reportRequest);
      setPayoutReconciliationSummaries(summary);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
  }, [reportRequest, timezone]);

  const loadConfigurations = async () => {
    const confs = await getMultipleConfigurations(['CONF_QBO_PAYOUT_SYNC_BETA', 'CONF_TIMEZONE']);
    const timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const enableQboPayoutSync = confs.find((c) => c.variable === 'CONF_QBO_PAYOUT_SYNC_BETA')?.value === '1';
    setEnableQboPayoutSync(enableQboPayoutSync);
    if (timezone) {
      setTimezone(timezone);
    }
  };

  // Hooks
  useEffect(() => {
    loadConfigurations();
  }, []);

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const file = await exportStripePayoutReconciliationReport({ from: reportRequest.from, to: reportRequest.to });

      const exportFileName = `Stripe_Payout_Report${
        reportRequest.from ? '_' + reportRequest.from?.toFormat('D').replaceAll('/', '-') : ''
      }`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e: any) {
      console.error(e);
      toast('Error exporting report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container-v2 pb-5">
      <div className="flex justify-between items-center">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Stripe Payout Reconciliation
        </Typography>
      </div>
      <div className="flex flex-column gap-4">
        <div className="flex flex-wrap item-start">
          <div className="m-[2px] flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(25%-4px)]">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              From
            </Typography>
            <DatePicker
              value={reportRequest.from}
              onChange={(from) =>
                setReportRequest((prevState) => ({
                  ...prevState,
                  from: from?.setZone(timezone, { keepLocalTime: true }).startOf('day'),
                }))
              }
              clearable={true}
            />
          </div>
          <div className="m-[2px] flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(25%-4px)]">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              To
            </Typography>
            <DatePicker
              value={reportRequest.to}
              onChange={(to) =>
                setReportRequest((prevState) => ({
                  ...prevState,
                  to: to?.setZone(timezone, { keepLocalTime: true }).endOf('day'),
                }))
              }
              clearable={true}
            />
          </div>
          <div className="m-[2px] self-end flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(50%-4px)]">
            <Button
              className="self-start lg:self-end	button-yellow !border-0 w-[114px] h-[40px]"
              onClick={() => exportReport()}
              data-cy="export"
            >
              <Typography
                type="text-md"
                weight="semi-bold"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Export
              </Typography>
              <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
            </Button>
          </div>
        </div>
        <div>
          {!isLoading && currency && payoutReconciliationSummaries && (
            <ReportSummaryStats summaries={payoutReconciliationSummaries} currency={currency} />
          )}
        </div>
        <Card>
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2">Available Date</th>
                <th className="text-left">Status</th>
                <th className="text-left">Stripe Id</th>
                <th className="text-right">Payments</th>
                <th className="text-right">Refunds</th>
                <th className="text-right">Fees</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={100}>
                    <Spinner />
                  </td>
                </tr>
              ) : (
                <>
                  {currency && payoutReconciliationReport && (
                    <>
                      {payoutReconciliationReport.items.length > 0 ? (
                        payoutReconciliationReport.items.map((report, i) => (
                          <TableRow
                            key={i}
                            report={report}
                            currency={currency}
                            enableQboPayoutSync={enableQboPayoutSync}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7}>No record found</td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
          <Paginator
            pagination={payoutReconciliationReport?.pagination}
            request={reportRequest}
            setRequest={(pag) =>
              setReportRequest((req) => {
                return { ...req, ...pag };
              })
            }
          />
        </Card>
      </div>
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Payout Reconciliation Report'} />
    </div>
  );
};

const TableRow = ({
  report,
  currency,
  enableQboPayoutSync,
}: {
  report: StripePayoutReconciliationReportRow;
  currency: string;
  enableQboPayoutSync: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  return (
    <>
      <tr className="cursor-pointer" onClick={() => setIsExpanded((e) => !e)}>
        <td className="py-2">{DateTime.fromISO(report['Available Date']).toFormat('D')}</td>
        <td>{StripePayoutStatusBadge(report['Status'])}</td>
        <td>{report['Stripe Id']}</td>
        <td className="text-right">{displayMoneyFormat(report['Payment Total'], currency)}</td>
        <td className="text-right">{displayMoneyFormat(report['Refunds Total'], currency)}</td>
        <td className="text-right">{displayMoneyFormat(report['Fee Total'], currency)}</td>
        <td className="text-right font-medium">{displayMoneyFormat(report['Amount'], currency)}</td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={100}>
            <PayoutDetail report={report} currency={currency} enableQboPayoutSync={enableQboPayoutSync} />
          </td>
        </tr>
      )}
    </>
  );
};

const PayoutDetail = ({
  report,
  currency,
  enableQboPayoutSync,
}: {
  report: StripePayoutReconciliationReportRow;
  currency: string;
  enableQboPayoutSync: boolean;
}) => {
  const { stripePayoutTransactionReport, exportStripePayoutTransactionReport } = useReportApiV2();
  // @hipaa-exclude-start
  const { syncPayout } = useQboApi();
  const tc = useContext(TenantContext);
  const qboEnabled = tc.configuration?.qboEnabled || false;
  // @hipaa-exclude-end
  const { hasPermissions } = useAdmin();
  const [transactions, setTransactions] = useState<StripePayoutReconciliationReportTransaction[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSyncingPayout, setIsSyncingPayout] = useState<boolean>(false);

  async function loadReport() {
    setIsLoading(true);
    const transactions = await stripePayoutTransactionReport(report['Stripe Id']);
    setTransactions(transactions);
    setIsLoading(false);
  }

  useEffect(() => {
    loadReport().catch((e) => {
      console.error(e);
      toast('Error loading report transactions', 'error');
    });
  }, []);

  const exportReport = async () => {
    const file = await exportStripePayoutTransactionReport(report['Stripe Id']);

    const exportFileName = 'Stripe_Payout_Transaction_Report';
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
  };

  // @hipaa-exclude-start
  const syncQBO = async () => {
    try {
      setIsSyncingPayout(true);
      await syncPayout(report['Stripe Id']);
      toast('Successfully synced Stripe Payout to QBO');
    } catch (e: any) {
      console.error('Error syncing Stripe Payout to QBO', e);
      toast('Error syncing Stripe Payout to QBO', 'error');
    }
    setIsSyncingPayout(false);
  };
  // @hipaa-exclude-end

  return (
    <div className="bg-[var(--grey-100)] p-4 flex flex-column gap-4">
      {transactions && (
        <div className="flex flex-row justify-between">
          <LocationSummaryStats transactions={transactions} currency={currency} />
          <div className="m-[2px] self-end flex flex-row gap-2">
            {
              // @hipaa-exclude-start
              qboEnabled && enableQboPayoutSync && hasPermissions('QuickBooks Online') && (
                <Button
                  className="self-start lg:self-end	button-secondary"
                  disabled={isSyncingPayout}
                  onClick={() => syncQBO()}
                  data-cy="export"
                >
                  <Typography type="text-md" weight="semi-bold" className="text-[var(--grey-700)] w-[5rem]">
                    {isSyncingPayout ? <Spinner /> : 'Sync QBO'}
                  </Typography>
                </Button>
              )
              // @hipaa-exclude-end
            }
            <Button className="self-start lg:self-end	button-yellow" onClick={() => exportReport()} data-cy="export">
              <Typography type="text-md" weight="semi-bold" className="text-[var(--grey-700)]">
                Export Transactions
              </Typography>
              <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
            </Button>
          </div>
        </div>
      )}

      <Card>
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Stripe Id</th>
              <th className="text-left">Invoice Number</th>
              <th className="text-left">Customer</th>
              <th className="text-left">Location</th>
              <th className="text-left">Payment Method</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Fee</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <Spinner />}
            {transactions &&
              transactions.map((t, i) => (
                <tr key={i}>
                  <td className="Date">
                    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                      {DateTime.fromISO(t['Date']).toFormat('D')}
                    </Typography>
                    <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                      {DateTime.fromISO(t['Date']).toFormat('t')}
                    </Typography>
                  </td>
                  <td>{t['Stripe Id']}</td>
                  <td>{t['Invoice Number']}</td>
                  <td>{t['Customer']}</td>
                  <td>{t['Location']}</td>
                  <td>{PaymentMethodFormat('Stripe', t['Payment Method'])}</td>
                  <td className="text-right">{displayMoneyFormat(t['Amount'], currency)}</td>
                  <td className="text-right">{displayMoneyFormat(t['Fee'], currency)}</td>
                  <td className="text-right">{displayMoneyFormat(t['Net'], currency)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const ReportSummaryStats = ({
  summaries,
  currency,
}: {
  summaries: StripePayoutReconciliationReportSummary[];
  currency: string;
}) => {
  function summaryCard(summary: StripePayoutReconciliationReportSummary, key?: any) {
    return (
      <Card key={key} className="min-w-[calc(15%-0.25rem)] p-4 flex flex-column gap-2 text-[0.9rem]">
        <div className="text-[var(--grey-500)]">{summary.Location}</div>
        <ul className="list-none p-0">
          <li className="flex justify-between gap-2">
            <span>Payments</span>
            <span>{displayMoneyFormat(summary.Payments, currency)}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Refunds</span>
            <span>{displayMoneyFormat(summary.Refunds, currency)}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Fees</span>
            <span>{displayMoneyFormat(summary.Fees, currency)}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Net</span>
            <span>{displayMoneyFormat(summary.Net, currency)}</span>
          </li>
        </ul>
      </Card>
    );
  }

  return <div className="flex flex-wrap gap-1">{summaries.map((summary, i) => summaryCard(summary, i))}</div>;
};

const LocationSummaryStats = ({
  transactions,
  currency,
}: {
  transactions: StripePayoutReconciliationReportTransaction[];
  currency: string;
}) => {
  function summaryCard(name: string, transactions: StripePayoutReconciliationReportTransaction[], key?: any) {
    const paymentTotal = dineroSum(
      transactions
        .filter((t) => t.Amount > 0)
        .map((t) =>
          Dinero({
            amount: safeRound(t.Amount * 100),
            currency: currency as Dinero.Currency,
            precision: 2,
          }),
        ),
      currency as Dinero.Currency,
    );
    const refundTotal = dineroSum(
      transactions
        .filter((t) => t.Amount < 0)
        .map((t) =>
          Dinero({
            amount: safeRound(t.Amount * 100),
            currency: currency as Dinero.Currency,
            precision: 2,
          }),
        ),
      currency as Dinero.Currency,
    );
    const feeTotal = dineroSum(
      transactions.map((t) =>
        Dinero({
          amount: safeRound(t.Fee * 100),
          currency: currency as Dinero.Currency,
          precision: 2,
        }),
      ),
      currency as Dinero.Currency,
    );
    const amount = paymentTotal.subtract(feeTotal).add(refundTotal);

    return (
      <Card key={key} className="min-w-[calc(15%-0.25rem)] p-4 flex flex-column gap-2 text-[0.9rem]">
        <div className="text-[var(--grey-500)]">{name || 'Unknown'}</div>
        <ul className="list-none p-0">
          <li className="flex justify-between gap-2">
            <span>Payments</span>
            <span>{displayMoneyFormat(paymentTotal.toUnit(), currency)}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Refunds</span>
            <span>{displayMoneyFormat(refundTotal.toUnit(), currency)}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Fees</span>
            <span>{displayMoneyFormat(feeTotal.toUnit(), currency)}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Net</span>
            <span>{displayMoneyFormat(amount.toUnit(), currency)}</span>
          </li>
        </ul>
      </Card>
    );
  }

  const locationGroupedTransactions = _.groupBy(transactions, 'Location');
  return (
    <div className="flex flex-wrap gap-1">
      {Object.keys(locationGroupedTransactions).map((location, i) =>
        summaryCard(location, locationGroupedTransactions[location], i),
      )}
    </div>
  );
};

function StripePayoutStatusBadge(status: StripePayoutStatus): React.ReactNode {
  switch (status) {
    case 'paid':
      return <span className="status-badge status-badge-green capitalize">{status}</span>;
    case 'in_transit':
    case 'pending':
      return <span className="status-badge status-badge-grey capitalize">{status.replaceAll('_', ' ')}</span>;
    case 'canceled':
    case 'failed':
      return <span className="status-badge status-badge-red capitalize">{status}</span>;
  }
}
